<script setup lang="ts">
import { ref } from 'vue';
import { useToast } from '@/composables/useToast';
import { useGlobalState } from '@/composables/useGlobalState';

const props = defineProps<{
    isOpen: boolean;
}>();

const emit = defineEmits(['close', 'success']);

const { addToast } = useToast();
const { fetchUser } = useGlobalState();

const email = ref('');
const password = ref('');
const loading = ref(false);
const error = ref('');
const mode = ref<'login' | 'register'>('login');

const handleLogin = async () => {
    loading.value = true;
    error.value = '';
    
    try {
        const response = await fetch('/api/login', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email: email.value, password: password.value })
        });
        
        const data = await response.json();
        
        if (response.ok) {
            localStorage.setItem('apiToken', data.token);
            await fetchUser();
            addToast('Connexion réussie', 'success');
            emit('success');
            emit('close');
        } else {
            error.value = data.error || 'Identifiants invalides';
        }
    } catch (e) {
        error.value = 'Une erreur est survenue';
    } finally {
        loading.value = false;
    }
};

const handleRegister = async () => {
    loading.value = true;
    error.value = '';
    
    try {
        const response = await fetch('/api/register', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email: email.value, password: password.value })
        });
        
        const data = await response.json();
        
        if (response.ok) {
            localStorage.setItem('apiToken', data.token);
            await fetchUser();
            addToast('Compte créé avec succès', 'success');
            emit('success');
            emit('close');
        } else {
            error.value = data.error || 'Erreur lors de l\'inscription';
        }
    } catch (e) {
        error.value = 'Une erreur est survenue';
    } finally {
        loading.value = false;
    }
};
</script>

<template>
    <div v-if="isOpen" class="fixed inset-0 z-[100] flex items-center justify-center p-4">
        <!-- Backdrop -->
        <div class="absolute inset-0 bg-black/60 backdrop-blur-sm" @click="emit('close')"></div>
        
        <!-- Modal -->
        <div class="relative w-full max-w-md bg-zinc-900 border border-zinc-800 rounded-3xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200">
            <div class="p-8">
                <div class="text-center mb-8">
                    <div class="inline-flex items-center justify-center w-16 h-16 bg-orange-500/10 rounded-2xl mb-4">
                        <i class="fas fa-running text-orange-500 text-2xl"></i>
                    </div>
                    <h2 class="text-2xl font-bold text-white">{{ mode === 'login' ? 'Connexion Sport' : 'Inscription Sport' }}</h2>
                    <p class="text-zinc-500 text-sm mt-1">
                        {{ mode === 'login' ? 'Accédez à votre compte Codifyo Sport' : 'Créez votre compte pour gérer vos séances' }}
                    </p>
                </div>

                <form @submit.prevent="mode === 'login' ? handleLogin() : handleRegister()" class="space-y-4">
                    <div>
                        <label class="block text-[10px] font-bold uppercase tracking-widest text-zinc-500 mb-1 ml-1">Email</label>
                        <input v-model="email" type="email" required 
                            class="w-full bg-zinc-800/50 border border-zinc-700 rounded-xl px-4 py-3 text-white placeholder:text-zinc-600 focus:outline-none focus:ring-2 focus:ring-orange-500/50 transition-all"
                            placeholder="votre@email.com">
                    </div>
                    
                    <div>
                        <label class="block text-[10px] font-bold uppercase tracking-widest text-zinc-500 mb-1 ml-1">Mot de passe</label>
                        <input v-model="password" type="password" required 
                            class="w-full bg-zinc-800/50 border border-zinc-700 rounded-xl px-4 py-3 text-white placeholder:text-zinc-600 focus:outline-none focus:ring-2 focus:ring-orange-500/50 transition-all"
                            placeholder="••••••••">
                        <p v-if="mode === 'register'" class="text-[10px] text-zinc-500 mt-2 ml-1">Minimum 6 caractères</p>
                    </div>

                    <div v-if="error" class="p-3 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400 text-xs font-bold text-center">
                        {{ error }}
                    </div>

                    <button :disabled="loading" type="submit" 
                        class="w-full bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white font-bold py-3 rounded-xl shadow-lg shadow-orange-500/20 transition-all active:scale-[0.98]">
                        {{ loading ? (mode === 'login' ? 'Connexion...' : 'Inscription...') : (mode === 'login' ? 'Se connecter' : 'Créer un compte') }}
                    </button>
                </form>

                <div class="mt-6 text-center">
                    <button @click="mode = mode === 'login' ? 'register' : 'login'" class="text-sm text-zinc-500 hover:text-orange-500 transition-colors">
                        {{ mode === 'login' ? 'Pas encore de compte ? S\'inscrire' : 'Déjà un compte ? Se connecter' }}
                    </button>
                </div>
            </div>
            
            <button @click="emit('close')" class="absolute top-4 right-4 text-zinc-500 hover:text-white transition-colors">
                <i class="fas fa-times"></i>
            </button>
        </div>
    </div>
</template>
